'use client';
import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useLanguage } from '@/lib/LanguageContext';
import { getTranslations } from '@/lib/i18n';

// metadata removed - client component

const capabilities = [
  {
    icon: 'fas fa-user-shield',
    titleEs: 'Evaluación de Vulnerabilidades',
    titleFr: 'Évaluation des Vulnérabilités',
    descriptionEs:
      'Identificamos y clasificamos las vulnerabilidades de tu infraestructura con análisis técnicos profundos, entregando reportes accionables con prioridades claras.',
    descriptionFr:
      'Nous identifions et classifions les vulnérabilités de votre infrastructure avec des analyses techniques approfondies, en fournissant des rapports actionnables avec des priorités claires.',
  },
  {
    icon: 'fas fa-lock',
    titleEs: 'Protección de Endpoints',
    titleFr: 'Protection des Endpoints',
    descriptionEs:
      'Implementamos soluciones de seguridad en dispositivos, servidores y estaciones de trabajo para prevenir accesos no autorizados y malware avanzado.',
    descriptionFr:
      'Nous mettons en œuvre des solutions de sécurité sur les appareils, serveurs et postes de travail pour prévenir les accès non autorisés et les malwares avancés.',
  },
  {
    icon: 'fas fa-eye',
    titleEs: 'Monitoreo Continuo',
    titleFr: 'Surveillance Continue',
    descriptionEs:
      'Vigilancia 24/7 de tu red y sistemas con alertas en tiempo real, detección de anomalías y respuesta rápida ante incidentes de seguridad.',
    descriptionFr:
      'Surveillance 24/7 de votre réseau et systèmes avec alertes en temps réel, détection d\'anomalies et réponse rapide aux incidents de sécurité.',
  },
  {
    icon: 'fas fa-file-contract',
    titleEs: 'Cumplimiento Regulatorio',
    titleFr: 'Conformité Réglementaire',
    descriptionEs:
      'Asesoramos en el cumplimiento de la Ley de Ciberseguridad chilena, ISO 27001, NIST y otros marcos normativos aplicables a tu sector.',
    descriptionFr:
      'Nous conseillons sur la conformité à la loi chilienne sur la cybersécurité, ISO 27001, NIST et autres cadres normatifs applicables à votre secteur.',
  },
  {
    icon: 'fas fa-cloud-upload-alt',
    titleEs: 'Seguridad en la Nube',
    titleFr: 'Sécurité dans le Cloud',
    descriptionEs:
      'Configuración segura de entornos cloud (AWS, Azure, GCP), control de accesos, cifrado de datos y auditorías de configuración continuas.',
    descriptionFr:
      'Configuration sécurisée des environnements cloud (AWS, Azure, GCP), contrôle des accès, chiffrement des données et audits de configuration continus.',
  },
  {
    icon: 'fas fa-graduation-cap',
    titleEs: 'Concientización y Formación',
    titleFr: 'Sensibilisation et Formation',
    descriptionEs:
      'Programas de capacitación para equipos sobre phishing, ingeniería social, buenas prácticas y cultura de seguridad organizacional.',
    descriptionFr:
      'Programmes de formation pour les équipes sur le phishing, l\'ingénierie sociale, les bonnes pratiques et la culture de sécurité organisationnelle.',
  },
];

const threats = [
  {
    icon: 'fas fa-bug',
    labelEs: 'Malware & Ransomware',
    labelFr: 'Malware & Ransomware',
  },
  {
    icon: 'fas fa-fish',
    labelEs: 'Phishing',
    labelFr: 'Phishing',
  },
  {
    icon: 'fas fa-network-wired',
    labelEs: 'Ataques de Red',
    labelFr: 'Attaques Réseau',
  },
  {
    icon: 'fas fa-user-secret',
    labelEs: 'Amenazas Internas',
    labelFr: 'Menaces Internes',
  },
  {
    icon: 'fas fa-database',
    labelEs: 'Fuga de Datos',
    labelFr: 'Fuite de Données',
  },
  {
    icon: 'fas fa-bomb',
    labelEs: 'Ataques DDoS',
    labelFr: 'Attaques DDoS',
  },
];

export default function CiberseguridadPage() {
  const { language } = useLanguage();
  const t = getTranslations(language);
  const s = t?.services;
  const isFr = language === 'fr';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Nunito Sans', 'Raleway', arial, sans-serif" }}>
      <Navbar />
      <section className="relative overflow-hidden" style={{ paddingTop: '160px', paddingBottom: '80px', backgroundColor: '#0a0a0a' }}>
        <div className="absolute top-20 right-10 opacity-20 hidden lg:block">
          <img src="https://innobox.cl/wp-content/uploads/2021/07/Dots-4.svg" alt="" className="w-40 h-40" />
        </div>
        <div className="absolute bottom-0 left-0 opacity-10 hidden lg:block">
          <img src="https://innobox.cl/wp-content/uploads/2021/07/Abstract-Circle.svg" alt="" className="w-64 h-64" />
        </div>
        <div className="max-w-[1200px] mx-auto px-[35px] relative z-10">
          <nav className="mb-8 flex items-center gap-2 text-sm" style={{ color: '#888' }}>
            <Link href="/" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbHome}</Link>
            <span>/</span>
            <Link href="/#servicios" className="hover:text-[#1AEF0F] transition-colors">{s?.breadcrumbServices}</Link>
            <span>/</span>
            <span style={{ color: '#1AEF0F' }}>{isFr ? 'Cybersécurité' : 'Ciberseguridad'}</span>
          </nav>
          <div className="flex flex-col lg:flex-row items-start gap-12">
            <div className="flex-1">
              <div className="inline-flex items-center gap-2 mb-6 px-4 py-2" style={{ border: '1px solid #1AEF0F', color: '#1AEF0F', fontSize: '13px', letterSpacing: '2px' }}>
                <i className="fab fa-cloudflare" />
                <span>{isFr ? 'PROTECTION NUMÉRIQUE' : 'PROTECCIÓN DIGITAL'}</span>
              </div>
              <h1 className="text-white font-bold mb-6" style={{ fontSize: 'clamp(32px, 4vw, 56px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.15' }}>
                {isFr ? 'Cyber' : 'Ciber'}<span style={{ color: '#1AEF0F' }}>{isFr ? 'sécurité' : 'seguridad'}</span>
              </h1>
              <p className="mb-8" style={{ fontSize: '18px', color: '#aaa', lineHeight: '1.7', maxWidth: '560px' }}>
                {isFr ? 'Protection intégrale et conformité réglementaire pour les organisations qui ne peuvent pas se permettre une faille de sécurité. Nous défendons votre infrastructure avec une technologie de pointe.' : 'Protección integral y cumplimiento normativo para organizaciones que no pueden permitirse una brecha de seguridad. Defendemos tu infraestructura con tecnología de vanguardia.'}
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="https://calendar.app.google/qrMMuz1WHJJ4eoab8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative inline-flex items-center overflow-hidden"
                  style={{
                    border: '1px solid #1AEF0F',
                    padding: '14px 32px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    color: '#1AEF0F',
                    backgroundColor: 'transparent',
                    transition: 'all 300ms ease-out',
                  }}
                >
                  <span className="relative z-10 transition-colors duration-300 group-hover:text-black">{isFr ? 'Évaluer ma Sécurité' : 'Evaluar mi Seguridad'}</span>
                  <span
                    className="absolute inset-0 transform translate-x-full group-hover:translate-x-0 transition-transform duration-300"
                    style={{ backgroundColor: '#1AEF0F', zIndex: 0 }}
                  />
                </a>
                <Link
                  href="/#servicios"
                  className="inline-flex items-center gap-2 text-white hover:text-[#1AEF0F] transition-colors"
                  style={{ fontSize: '15px', padding: '14px 0' }}
                >
                  <i className="fas fa-arrow-left text-sm" />
                  {isFr ? 'Voir tous les services' : 'Ver todos los servicios'}
                </Link>
              </div>
            </div>
            <div className="lg:w-72 grid grid-cols-2 gap-4">
              {[
                { value: '24/7', labelEs: 'Monitoreo continuo', labelFr: 'Surveillance continue' },
                { value: 'ISO', labelEs: '27001 alineado', labelFr: '27001 aligné' },
                { value: '0', labelEs: 'Tolerancia a brechas', labelFr: 'Tolérance aux failles' },
                { value: '+5', labelEs: 'Años en ciberseguridad', labelFr: 'Ans en cybersécurité' },
              ]?.map((stat, i) => (
                <div key={i} className="p-5" style={{ border: '1px solid #222', backgroundColor: '#111' }}>
                  <div className="font-bold mb-1" style={{ fontSize: '28px', color: '#1AEF0F', fontFamily: "'Titillium Web', sans-serif" }}>
                    {stat?.value}
                  </div>
                  <div style={{ fontSize: '12px', color: '#888', lineHeight: '1.4' }}>
                    {isFr ? stat?.labelFr : stat?.labelEs}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '80px', paddingBottom: '80px', backgroundColor: '#fff' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <div className="mb-12">
            <h2 className="font-bold text-black mb-3" style={{ fontSize: 'clamp(24px, 2.5vw, 36px)', fontFamily: "'Titillium Web', sans-serif" }}>
              {s?.capabilitiesTitle}
            </h2>
            <p style={{ fontSize: '16px', color: '#505050', maxWidth: '600px', lineHeight: '1.6' }}>
              {isFr ? 'Solutions de cybersécurité de bout en bout qui protègent chaque couche de votre organisation, du dispositif au cloud.' : 'Soluciones de ciberseguridad end-to-end que protegen cada capa de tu organización, desde el dispositivo hasta la nube.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
            {capabilities?.map((cap, i) => (
              <div key={i} className="group p-8 transition-all duration-300 hover:bg-[#f9f9f9]" style={{ borderBottom: '1px solid #E2E2E2', borderRight: i % 3 !== 2 ? '1px solid #E2E2E2' : 'none' }}>
                <div className="mb-5 flex items-center justify-center" style={{ width: '52px', height: '52px', border: '1px solid #E2E2E2', borderRadius: '50%' }}>
                  <i className={`${cap?.icon} text-xl text-black group-hover:text-[#1AEF0F] transition-colors duration-300`} />
                </div>
                <h3 className="font-semibold text-black mb-3" style={{ fontSize: '17px', fontFamily: "'Titillium Web', sans-serif" }}>
                  {isFr ? cap?.titleFr : cap?.titleEs}
                </h3>
                <p style={{ fontSize: '14px', color: '#505050', lineHeight: '1.6' }}>
                  {isFr ? cap?.descriptionFr : cap?.descriptionEs}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '60px', paddingBottom: '80px', backgroundColor: '#0a0a0a' }}>
        <div className="max-w-[1200px] mx-auto px-[35px]">
          <h2 className="font-bold text-white mb-10" style={{ fontSize: 'clamp(22px, 2vw, 30px)', fontFamily: "'Titillium Web', sans-serif" }}>
            {isFr ? 'Menaces que Nous Neutralisons' : 'Amenazas que Neutralizamos'}
          </h2>
          <div className="flex flex-wrap gap-4">
            {threats?.map((th, i) => (
              <div key={i} className="flex items-center gap-3 px-6 py-4" style={{ border: '1px solid #222', backgroundColor: '#111' }}>
                <i className={`${th?.icon} text-[#1AEF0F]`} />
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#ccc' }}>{isFr ? th?.labelFr : th?.labelEs}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section style={{ paddingTop: '70px', paddingBottom: '70px', backgroundColor: '#1AEF0F' }}>
        <div className="max-w-[1200px] mx-auto px-[35px] flex flex-col sm:flex-row items-center justify-between gap-6">
          <p
            className="text-black font-bold m-0"
            style={{ fontSize: 'clamp(18px, 2vw, 26px)', fontFamily: "'Titillium Web', sans-serif", lineHeight: '1.3' }}
          >
            {isFr ? 'Votre organisation est-elle protégée contre les menaces actuelles ?' : '¿Tu organización está protegida ante las amenazas actuales?'}
          </p>
          <Link
            href="/contact"
            className="group relative inline-flex items-center overflow-hidden border border-black text-black bg-transparent flex-shrink-0"
            style={{ padding: '14px 32px', fontSize: '15px', fontWeight: 'bold', transition: 'all 300ms ease-out' }}
          >
            <span className="relative z-10 transition-colors duration-300 group-hover:text-white">
              {s?.ctaBtnContact}
            </span>
            <span
              className="absolute inset-0 bg-black transform translate-x-full group-hover:translate-x-0 transition-transform duration-300"
              style={{ zIndex: 0 }}
            />
          </Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
